import React from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { trapiTokenRequest } from '../config/msal';

const ADMIN_GROUP_ID = (import.meta.env.VITE_ADMIN_GROUP_ID as string) || 'eea9dc15-95f3-47fd-8250-5206012caf57';

type SwaClientPrincipal = {
  userId?: string;
  identityProvider?: string;
  userDetails?: string;
  userRoles?: string[];
  claims?: Array<{ typ?: string; val?: string } | Record<string, unknown>>;
} | null;

type SwaAuthResponseObject = { clientPrincipal: SwaClientPrincipal };
type SwaAuthResponseArray = Array<NonNullable<SwaClientPrincipal>>; // legacy/alt shape

export function useSwaAuth() {
  const { accounts, instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [roles, setRoles] = React.useState<string[] | null>(null);
  const [swaLoading, setSwaLoading] = React.useState(true);
  const [msalTokenLoading, setMsalTokenLoading] = React.useState(true);
  const [swaHasAdminGroup, setSwaHasAdminGroup] = React.useState<boolean>(false);
  const [msalAtHasAdminGroup, setMsalAtHasAdminGroup] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.auth/me', { credentials: 'include' });
        if (!res.ok) throw new Error('auth me not available');
        const json = await res.json();

        // Support both shapes: object { clientPrincipal } and [ { ... } ]
        let principal: SwaClientPrincipal = null;
        if (json && typeof json === 'object' && 'clientPrincipal' in json) {
          principal = (json as SwaAuthResponseObject).clientPrincipal ?? null;
        } else if (Array.isArray(json)) {
          principal = (json as SwaAuthResponseArray)[0] ?? null;
        }

  const userRoles = (principal?.userRoles ?? []) as string[];
        const claims = (principal?.claims ?? []) as Array<any>;
        const groupsFromClaims: string[] = claims
          .map((c: any) => {
            const typ: string | undefined = c?.typ;
            const val: string | undefined = c?.val;
            if (!typ || !val) return undefined;
            const isGroupClaim = typ === 'groups' ||
              typ === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups' ||
              typ === 'groups:full';
            return isGroupClaim ? val : undefined;
          })
          .filter((g: string | undefined): g is string => typeof g === 'string');

        if (!cancelled) {
          const adminLower = ADMIN_GROUP_ID.toLowerCase();
          const rolesLower = userRoles.map(r => (typeof r === 'string' ? r.toLowerCase() : ''));
          const claimsLower = groupsFromClaims.map(g => (typeof g === 'string' ? g.toLowerCase() : ''));
          setRoles(userRoles);
          setSwaHasAdminGroup(rolesLower.includes(adminLower) || claimsLower.includes(adminLower));
        }
      } catch {
        if (!cancelled) {
          setRoles(null);
          setSwaHasAdminGroup(false);
        }
      } finally {
        if (!cancelled) setSwaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull group membership from MSAL id token if available
  const idTokenClaims = (accounts?.[0] as any)?.idTokenClaims as Record<string, unknown> | undefined;
  const msalGroups: string[] | undefined = idTokenClaims?.groups as string[] | undefined;
  const msalRoles: string[] | undefined = idTokenClaims?.roles as string[] | undefined; // some tenants map to roles
  const adminLower = ADMIN_GROUP_ID.toLowerCase();
  const isAdminFromMsal = (
    (Array.isArray(msalGroups) && msalGroups.map(g => g.toLowerCase()).includes(adminLower)) ||
    (Array.isArray(msalRoles) && msalRoles.map(r => r.toLowerCase()).includes(adminLower))
  );

  // Fallback: inspect the access token for groups if id token lacks them.
  // Wait for MSAL to finish initializing before checking.
  const msalReady = inProgress === InteractionStatus.None;
  React.useEffect(() => {
    if (!msalReady) return; // MSAL still initializing — don't resolve yet
    let cancelled = false;
    (async () => {
      try {
        const account = accounts?.[0] ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        if (!account) {
          if (!cancelled) setMsalTokenLoading(false);
          return;
        }
        let res;
        try {
          res = await instance.acquireTokenSilent({ ...trapiTokenRequest, account });
        } catch {
          // Silent acquisition failed — skip admin-from-token check rather than
          // opening a popup from a background effect (which browsers block).
          if (!cancelled) setMsalAtHasAdminGroup(false);
          if (!cancelled) setMsalTokenLoading(false);
          return;
        }
        const token = res.accessToken || '';
        const parts = token.split('.');
        if (parts.length < 2) {
          if (!cancelled) setMsalTokenLoading(false);
          return;
        }
        // base64url decode
        const decode = (s: string) => {
          const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
          const pad = '='.repeat((4 - (b64.length % 4)) % 4);
          const str = atob(b64 + pad);
          try { return decodeURIComponent(Array.prototype.map.call(str, (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')); } catch { return str; }
        };
        const payloadRaw = decode(parts[1]);
        const payload = JSON.parse(payloadRaw || '{}') as { groups?: string[] };
        const groups = Array.isArray(payload.groups) ? payload.groups : [];
        const has = groups.map(g => (g || '').toLowerCase()).includes(adminLower);
        if (!cancelled) setMsalAtHasAdminGroup(has);
      } catch {
        if (!cancelled) setMsalAtHasAdminGroup(false);
      } finally {
        if (!cancelled) setMsalTokenLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accounts, instance, msalReady]);

  const isAdminFromSwa = swaHasAdminGroup;
  const isAdmin = !!(isAdminFromSwa || isAdminFromMsal || msalAtHasAdminGroup);

  // Loading until both SWA and MSAL token checks complete
  const loading = swaLoading || msalTokenLoading;

  return { roles, loading, isAdmin, ADMIN_GROUP_ID } as const;
}
